import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { isGenericHttpFailureMessage, isHttpClientError } from "../../../lib/http/errors";
import {
  createCustomizationDemand,
  createCustomizationTrace,
  fetchCustomizationDemand,
  fetchCustomizationProjectEditOptions,
  fetchCustomizationProjectPointNames,
  fetchCustomizationReferenceDemands,
  fetchCustomizationWorkbench,
  importLegacyCustomizationFromRoute as importLegacyCustomizationFromRouteFromRepo,
  notifyCustomizationDispatcher,
  previewCustomizationRouteLink,
  resolveCustomizationOpportunityBinding,
  startCustomizationItineraryGeneration,
  validateCustomizationItineraryGeneration,
} from "../repo/customizationRepo";
import {
  getActualDemandRequiredMissingFields,
  getDemandRequiredStatus,
  normalizeDemandReviewReasons,
} from "../service/customizationService";
import { findLegacyJdReferenceKey, parseLegacyJdReference } from "../utils/legacyJd";
import { useQuickItineraryDetails } from "./useQuickItineraryDetails";
import type {
  CreateCustomizationDemandPayload,
  CreateCustomizationDemandResult,
  CustomizationDemandDraft,
  CustomizationDemandVersionOption,
  CustomizationProjectSnapshot,
  CustomizationProjectUpdatePayload,
  CustomizationReferenceDemandCandidate,
  CustomizationRouteLinkRecognitionResult,
  CustomizationOpportunityBinding,
  CustomizationOpportunityBindingDecision,
  CustomizationTraceCreate,
  CustomizationTraceItem,
  CustomizationWorkbench,
  LegacyCustomizeRouteImportPayload,
} from "../types";

const customizationWorkbenchQueryRootKey = ["customization", "workbench"] as const;
const LEGACY_ROUTE_LINK_HINT_PATTERN =
  /https?:\/\/www\.yishulvxing\.com\/Form_New\/Article\/ArticleRouteDetailP_New\.aspx\?(?:[^#\s]*&)?(?:ID|ArticleId)=/i;
const DEMAND_VERSION_SWITCH_VISIBLE_MS = 480;

type CustomizationWorkbenchRuntimeOptions = {
  canEditQuickItinerary?: boolean;
  quickItineraryViewerId?: string;
  opportunityId?: number;
  legacyJiDiaoNo?: string;
  hideLinkedItineraryTraces?: boolean;
  onProjectChanged?: (projectId: string) => void;
  onNewCustomizationCreated?: (projectId: string) => void;
};

type ItineraryEditorTarget =
  | { type: "existing"; demandId: string }
  | { type: "manual"; demandId: string }
  | { type: "generate"; demandId: string };

type ProjectSaveOptions = {
  silent?: boolean;
  optimistic?: boolean;
};

/** 从旧系统外部入口导入全新定制项目。 */
export function importLegacyCustomizationFromRoute(
  payload: LegacyCustomizeRouteImportPayload,
) {
  return importLegacyCustomizationFromRouteFromRepo(payload);
}

/** 管理需求定制工作台查询、追溯选择与版本生成。 */
export function useCustomizationWorkbenchRuntime(
  projectId: string,
  options: CustomizationWorkbenchRuntimeOptions = {},
) {
  const opportunityId = options.opportunityId;
  const loadWorkbench = (targetProjectId: string) =>
    opportunityId === undefined
      ? fetchCustomizationWorkbench(targetProjectId)
      : fetchCustomizationWorkbench(targetProjectId, { opportunityId });
  const queryClient = useQueryClient();
  const [selectedTraceIds, setSelectedTraceIds] = useState<string[]>([]);
  const [localProject, setLocalProject] = useState<CustomizationProjectSnapshot | null>(null);
  const [localDemand, setLocalDemand] = useState<CustomizationDemandDraft | null>(null);
  const [viewingDemand, setViewingDemand] = useState<CustomizationDemandDraft | null>(null);
  const [manualDraftPatch, setManualDraftPatch] = useState<CustomizationProjectUpdatePayload | null>(null);
  const [manualDraftBaseDemandId, setManualDraftBaseDemandId] = useState<string | null>(null);
  const [itineraryEditorTarget, setItineraryEditorTarget] =
    useState<ItineraryEditorTarget | null>(null);
  const [itineraryEditorTargetsByDemandId, setItineraryEditorTargetsByDemandId] = useState<
    Record<string, ItineraryEditorTarget>
  >({});
  const [localDemandVersions, setLocalDemandVersions] = useState<
    CustomizationDemandVersionOption[] | null
  >(null);
  const [localTraces, setLocalTraces] = useState<CustomizationTraceItem[] | null>(null);
  const [switchingDemandVersionId, setSwitchingDemandVersionId] = useState<string | null>(null);
  const [legacyVersionLoadingId, setLegacyVersionLoadingId] = useState<string | null>(null);
  const [referenceCandidates, setReferenceCandidates] = useState<CustomizationReferenceDemandCandidate[]>([]);
  const [routeLinkPreview, setRouteLinkPreview] =
    useState<CustomizationRouteLinkRecognitionResult | null>(null);
  const [routeLinkPreviewing, setRouteLinkPreviewing] = useState(false);
  const [traceReferenceCandidatesByTraceId, setTraceReferenceCandidatesByTraceId] = useState<
    Record<string, CustomizationReferenceDemandCandidate[]>
  >({});
  const [traceRouteLinkPreviewByTraceId, setTraceRouteLinkPreviewByTraceId] = useState<
    Record<string, CustomizationRouteLinkRecognitionResult>
  >({});
  const [selectedReferenceDemandId, setSelectedReferenceDemandId] = useState<string | null>(null);
  const [referenceErrorMessage, setReferenceErrorMessage] = useState("");
  const [routeLinkErrorMessage, setRouteLinkErrorMessage] = useState("");
  const [mutationErrorMessage, setMutationErrorMessage] = useState("");
  const [localOpportunityBinding, setLocalOpportunityBinding] =
    useState<CustomizationOpportunityBinding | null>(null);
  const [opportunityBindingOpen, setOpportunityBindingOpen] = useState(false);
  const [opportunityBindingPreparing, setOpportunityBindingPreparing] = useState(false);
  const [opportunityGateRefreshing, setOpportunityGateRefreshing] = useState(false);
  const dismissedOpportunityBindingKeyRef = useRef("");
  const preparedOpportunityBindingKeyRef = useRef("");
  const traceReferenceCacheRef = useRef<Record<string, CustomizationReferenceDemandCandidate[]>>({});
  const traceRouteLinkPreviewCacheRef = useRef<
    Record<string, CustomizationRouteLinkRecognitionResult | null>
  >({});
  const routeLinkPreviewRequestRef = useRef(0);
  const appliedLegacyVersionRef = useRef("");
  const failedLegacyVersionRef = useRef("");

  const query = useQuery({
    queryKey: [...customizationWorkbenchQueryRootKey, projectId, opportunityId ?? null],
    queryFn: () => loadWorkbench(projectId),
  });

  const workbench = query.data;
  const opportunityBinding = localOpportunityBinding ?? workbench?.opportunityBinding ?? null;
  const persistedProject = localProject ?? workbench?.project ?? null;
  const persistedCurrentDemand = localDemand ?? workbench?.currentDemand ?? null;
  const baseDisplayDemand = viewingDemand ?? persistedCurrentDemand;
  const isManualDraftActive = Boolean(
    manualDraftPatch &&
      manualDraftBaseDemandId &&
      baseDisplayDemand?.id &&
      manualDraftBaseDemandId === baseDisplayDemand.id,
  );
  const manualDraftFieldCount =
    isManualDraftActive && manualDraftPatch
      ? Object.values(manualDraftPatch).filter((value) => value !== undefined).length
      : 0;
  const project = useMemo(
    () =>
      isManualDraftActive && persistedProject && manualDraftPatch
        ? applyProjectPayloadToSnapshot(persistedProject, manualDraftPatch)
        : persistedProject,
    [isManualDraftActive, manualDraftPatch, persistedProject],
  );
  const resolvedProjectId = project?.id ?? projectId;
  const currentDemand = persistedCurrentDemand;
  const displayDemand = useMemo(
    () =>
      isManualDraftActive && baseDisplayDemand && project && manualDraftPatch
        ? applyManualDraftToDemand(baseDisplayDemand, manualDraftPatch, project, persistedProject)
        : baseDisplayDemand,
    [baseDisplayDemand, isManualDraftActive, manualDraftPatch, persistedProject, project],
  );
  const quickItinerary = useQuickItineraryDetails(resolvedProjectId, displayDemand?.id, {
    enabled: options.canEditQuickItinerary ?? false,
    viewerId: options.quickItineraryViewerId,
    switching: Boolean(switchingDemandVersionId || legacyVersionLoadingId),
  });
  const demandVersions = useMemo(
    () =>
      mergeDemandVersionRequiredStatuses(
        localDemandVersions ?? workbench?.demandVersions ?? [],
        [currentDemand, viewingDemand, displayDemand],
      ),
    [currentDemand, displayDemand, localDemandVersions, viewingDemand, workbench?.demandVersions],
  );
  const rawTraces = localTraces ?? workbench?.traces ?? [];
  const traces = useMemo(
    () => rawTraces.filter((trace) => trace.sourceType !== "linked_itinerary"),
    [rawTraces],
  );
  const selectableTraceIds = useMemo(
    () => new Set(traces.filter(isTraceSelectable).map((trace) => trace.id)),
    [traces],
  );
  const selectableSelectedTraceIds = useMemo(
    () => selectedTraceIds.filter((traceId) => selectableTraceIds.has(traceId)),
    [selectedTraceIds, selectableTraceIds],
  );
  const latestDemandVersion = useMemo(
    () => getLatestDemandVersion(demandVersions),
    [demandVersions],
  );
  const displayDemandVersion = useMemo(
    () =>
      displayDemand?.id
        ? demandVersions.find((version) => version.id === displayDemand.id) ?? null
        : null,
    [demandVersions, displayDemand?.id],
  );
  const historicalGenerationBase =
    displayDemand?.id &&
    latestDemandVersion?.id &&
    displayDemand.id !== latestDemandVersion.id
      ? {
          id: displayDemand.id,
          versionNumber:
            displayDemand.versionNumber ??
            displayDemandVersion?.versionNumber ??
            `V${displayDemand.versionSeq ?? ""}`,
        }
      : null;
  const legacyTargetVersion = useMemo(
    () => resolveLegacyDemandVersion(options.legacyJiDiaoNo, demandVersions, currentDemand),
    [currentDemand, demandVersions, options.legacyJiDiaoNo],
  );
  const legacyTargetApplyKey = legacyTargetVersion
    ? `${resolvedProjectId}:${options.legacyJiDiaoNo ?? "latest-available"}:${legacyTargetVersion.id}`
    : "";
  const isLegacyTargetVersionPending = Boolean(
    legacyTargetVersion &&
      legacyTargetApplyKey &&
      legacyTargetVersion.id !== displayDemand?.id &&
      appliedLegacyVersionRef.current !== legacyTargetApplyKey &&
      failedLegacyVersionRef.current !== legacyTargetApplyKey,
  );

  useEffect(() => {
    setLocalProject(null);
    setLocalDemand(null);
    setViewingDemand(null);
    setManualDraftPatch(null);
    setManualDraftBaseDemandId(null);
    setItineraryEditorTarget(null);
    setItineraryEditorTargetsByDemandId({});
    setLocalDemandVersions(null);
    setLocalTraces(null);
    setSwitchingDemandVersionId(null);
    setLegacyVersionLoadingId(null);
    setTraceReferenceCandidatesByTraceId({});
    setSelectedTraceIds([]);
    setReferenceCandidates([]);
    setRouteLinkPreview(null);
    setRouteLinkPreviewing(false);
    setSelectedReferenceDemandId(null);
    setTraceRouteLinkPreviewByTraceId({});
    setReferenceErrorMessage("");
    setRouteLinkErrorMessage("");
    setMutationErrorMessage("");
    setLocalOpportunityBinding(null);
    setOpportunityBindingOpen(false);
    setOpportunityBindingPreparing(false);
    setOpportunityGateRefreshing(false);
    dismissedOpportunityBindingKeyRef.current = "";
    preparedOpportunityBindingKeyRef.current = "";
    routeLinkPreviewRequestRef.current += 1;
    appliedLegacyVersionRef.current = "";
    failedLegacyVersionRef.current = "";
  }, [projectId, options.legacyJiDiaoNo]);

  useEffect(() => {
    if (
      !opportunityBinding ||
      !["binding_required", "review_required"].includes(opportunityBinding.status)
    ) {
      return;
    }
    const key = buildOpportunityBindingPresentationKey(opportunityBinding);
    if (dismissedOpportunityBindingKeyRef.current === key) {
      return;
    }

    // 归属 DTO 已在 create_demand / 工作台响应中带齐候选；直接打开弹窗，不再二次整页刷新。
    setOpportunityBindingOpen(true);
    preparedOpportunityBindingKeyRef.current = key;
    setOpportunityBindingPreparing(false);
  }, [opportunityBinding]);

  const selectedTraces = useMemo(
    () => traces.filter((trace) => selectableSelectedTraceIds.includes(trace.id)),
    [selectableSelectedTraceIds, traces],
  );

  const selectedReferenceDemand = useMemo(
    () =>
      referenceCandidates.find((candidate) => candidate.demandId === selectedReferenceDemandId) ??
      null,
    [referenceCandidates, selectedReferenceDemandId],
  );
  const traceReferenceKeyEntries = useMemo(
    () =>
      traces
        .map((trace) => ({
          traceId: trace.id,
          referenceKey: extractReferenceKey(`${trace.content}\n${trace.summary ?? ""}`),
        }))
        .filter(
          (entry): entry is { traceId: string; referenceKey: string } =>
            Boolean(entry.referenceKey),
        ),
    [traces],
  );
  const traceReferenceKeySignature =
    traceReferenceKeyEntries
      .map((entry) => `${entry.traceId}:${entry.referenceKey}`)
      .join("|");
  const traceReferenceEntriesBySignature = traceReferenceKeyEntries;
  const traceRouteLinkPreviewEntries = useMemo(
    () =>
      traces
        .filter((trace) => !extractReferenceKey(`${trace.content}\n${trace.summary ?? ""}`))
        .filter((trace) => hasLegacyRouteLinkHint(trace.content))
        .map((trace) => ({ traceId: trace.id, content: trace.content })),
    [traces],
  );

  const refreshWorkbenchVersionState = async (targetDemandId: string) => {
    try {
      const refreshed = await loadWorkbench(resolvedProjectId);
      if (!refreshed.demandVersions.some((version) => version.id === targetDemandId)) {
        return false;
      }
      setLocalProject(refreshed.project);
      setLocalDemand(refreshed.currentDemand);
      setLocalDemandVersions((current) =>
        mergeRefreshedDemandVersionOptions(refreshed.demandVersions, current ?? demandVersions),
      );
      setLocalTraces(refreshed.traces);
      setLocalOpportunityBinding(refreshed.opportunityBinding);
      queryClient.setQueryData<CustomizationWorkbench>(
        [...customizationWorkbenchQueryRootKey, resolvedProjectId, opportunityId ?? null],
        refreshed,
      );
      return true;
    } catch {
      // 切换版本时的工作台刷新只用于同步行程状态，不阻断版本详情展示。
      return false;
    }
  };

  const refreshWorkbenchForOpportunityGate = async () => {
    setOpportunityGateRefreshing(true);
    setMutationErrorMessage("");
    try {
      const refreshed = await loadWorkbench(resolvedProjectId);
      setLocalProject(refreshed.project);
      setLocalDemand(refreshed.currentDemand);
      setLocalDemandVersions(refreshed.demandVersions);
      setLocalTraces(refreshed.traces);
      setLocalOpportunityBinding(refreshed.opportunityBinding);
      queryClient.setQueryData<CustomizationWorkbench>(
        [...customizationWorkbenchQueryRootKey, resolvedProjectId, opportunityId ?? null],
        refreshed,
      );
      return refreshed.opportunityBinding;
    } catch {
      setMutationErrorMessage("商机归属状态刷新失败，请重试。");
      return undefined;
    } finally {
      setOpportunityGateRefreshing(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const traceIdsWithReference = new Set(
      traceReferenceEntriesBySignature.map((entry) => entry.traceId),
    );
    setTraceReferenceCandidatesByTraceId((current) =>
      areTraceReferenceIdsEqual(current, traceIdsWithReference)
        ? current
        : Object.fromEntries(
        Object.entries(current).filter(([traceId]) => traceIdsWithReference.has(traceId)),
          ),
    );

    for (const entry of traceReferenceEntriesBySignature) {
      const cacheKey = `${resolvedProjectId}:${entry.referenceKey}`;
      const cached = traceReferenceCacheRef.current[cacheKey];
      if (cached) {
        setTraceReferenceCandidatesByTraceId((current) => ({
          ...current,
          [entry.traceId]: cached,
        }));
        continue;
      }
      void fetchReferenceDemandItems(resolvedProjectId, entry.referenceKey)
        .then((items) => {
          traceReferenceCacheRef.current[cacheKey] = items;
          if (cancelled) {
            return;
          }
          setTraceReferenceCandidatesByTraceId((current) => ({
            ...current,
            [entry.traceId]: items,
          }));
        })
        .catch(() => {
          if (cancelled) {
            return;
          }
          setTraceReferenceCandidatesByTraceId((current) => ({
            ...current,
            [entry.traceId]: [],
          }));
        });
    }

    return () => {
      cancelled = true;
    };
  }, [resolvedProjectId, traceReferenceEntriesBySignature, traceReferenceKeySignature]);

  useEffect(() => {
    let cancelled = false;
    const traceIdsWithRouteLink = new Set(
      traceRouteLinkPreviewEntries.map((entry) => entry.traceId),
    );
    setTraceRouteLinkPreviewByTraceId((current) =>
      areTraceRouteLinkPreviewIdsEqual(current, traceIdsWithRouteLink)
        ? current
        : Object.fromEntries(
            Object.entries(current).filter(([traceId]) => traceIdsWithRouteLink.has(traceId)),
          ),
    );

    for (const entry of traceRouteLinkPreviewEntries) {
      const cacheKey = `${resolvedProjectId}:${entry.content}`;
      if (cacheKey in traceRouteLinkPreviewCacheRef.current) {
        const cached = traceRouteLinkPreviewCacheRef.current[cacheKey];
        if (cached) {
          setTraceRouteLinkPreviewByTraceId((current) => ({
            ...current,
            [entry.traceId]: cached,
          }));
        }
        continue;
      }

      void previewCustomizationRouteLink(resolvedProjectId, entry.content)
        .then((result) => {
          const preview = result.recognized && result.route ? result : null;
          traceRouteLinkPreviewCacheRef.current[cacheKey] = preview;
          if (cancelled) {
            return;
          }
          setTraceRouteLinkPreviewByTraceId((current) => {
            if (!preview) {
              return Object.fromEntries(
                Object.entries(current).filter(([traceId]) => traceId !== entry.traceId),
              );
            }
            return {
              ...current,
              [entry.traceId]: preview,
            };
          });
        })
        .catch(() => {
          traceRouteLinkPreviewCacheRef.current[cacheKey] = null;
          if (cancelled) {
            return;
          }
          setTraceRouteLinkPreviewByTraceId((current) => {
            return Object.fromEntries(
              Object.entries(current).filter(([traceId]) => traceId !== entry.traceId),
            );
          });
        });
    }

    return () => {
      cancelled = true;
    };
  }, [resolvedProjectId, traceRouteLinkPreviewEntries]);

  useEffect(() => {
    if (!legacyTargetVersion || !legacyTargetApplyKey) {
      return;
    }
    if (appliedLegacyVersionRef.current === legacyTargetApplyKey) {
      return;
    }
    if (legacyTargetVersion.id === displayDemand?.id) {
      failedLegacyVersionRef.current = "";
      appliedLegacyVersionRef.current = legacyTargetApplyKey;
      setLegacyVersionLoadingId(null);
      return;
    }
    if (legacyTargetVersion.id === currentDemand?.id) {
      setViewingDemand(null);
      failedLegacyVersionRef.current = "";
      appliedLegacyVersionRef.current = legacyTargetApplyKey;
      setLegacyVersionLoadingId(null);
      return;
    }
    let cancelled = false;
    failedLegacyVersionRef.current = "";
    setLegacyVersionLoadingId(legacyTargetVersion.id);
    void runMutation(
      () =>
        fetchCustomizationDemand(resolvedProjectId, legacyTargetVersion.id, {
          view: "workbench",
        }),
      setMutationErrorMessage,
    )
      .then((demand) => {
        if (cancelled) {
          return;
        }
        if (!demand) {
          failedLegacyVersionRef.current = legacyTargetApplyKey;
          return;
        }
        setLocalDemandVersions((current) =>
          mergeDemandVersionRequiredStatuses(current ?? demandVersions, [demand]),
        );
        setViewingDemand(demand);
        failedLegacyVersionRef.current = "";
        appliedLegacyVersionRef.current = legacyTargetApplyKey;
      })
      .finally(() => {
        if (!cancelled) {
          setLegacyVersionLoadingId((current) =>
            current === legacyTargetVersion.id ? null : current,
          );
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    currentDemand?.id,
    demandVersions,
    displayDemand?.id,
    legacyTargetApplyKey,
    legacyTargetVersion,
    resolvedProjectId,
    setMutationErrorMessage,
  ]);

  useEffect(() => {
    const displayDemandId = displayDemand?.id;
    if (!displayDemandId) {
      setItineraryEditorTarget(null);
      return;
    }
    const cachedTarget = itineraryEditorTargetsByDemandId[displayDemandId];
    // 仅保留用户主动触发的智能体/人工会话；有行程时工作台右侧改为统一预览，不再自动挂载嵌入编辑器。
    if (cachedTarget?.type === "generate" || cachedTarget?.type === "manual") {
      setItineraryEditorTarget((current) =>
        isSameItineraryEditorTarget(current, cachedTarget) ? current : cachedTarget,
      );
      return;
    }
    if (cachedTarget) {
      setItineraryEditorTarget((current) =>
        isSameItineraryEditorTarget(current, cachedTarget) ? current : cachedTarget,
      );
      return;
    }
    setItineraryEditorTarget(null);
  }, [
    displayDemand?.id,
    itineraryEditorTargetsByDemandId,
  ]);

  const traceMutation = useMutation({
    mutationFn: (payload: CustomizationTraceCreate) =>
      createCustomizationTrace(resolvedProjectId, payload),
    onSuccess(trace) {
      setLocalTraces((current) => [trace, ...(current ?? traces)]);
      queryClient.invalidateQueries({ queryKey: customizationWorkbenchQueryRootKey });
    },
  });

  const demandMutation = useMutation({
    mutationFn: (payload: CreateCustomizationDemandPayload) =>
      createCustomizationDemand(resolvedProjectId, payload),
    onSuccess(result, payload) {
      const isNewProject = payload.action === "create_new" && result.project.id !== resolvedProjectId;
      const shouldOpenNewCustomization = isNewProject && Boolean(payload.baseDemandId);
      const resultDemand = {
        ...result.demand,
        needsHumanReviewReasons:
          result.demand.needsHumanReviewReasons?.length
            ? result.demand.needsHumanReviewReasons
            : result.needsHumanReviewReasons,
      };
      const nextDemandVersions = mergeDemandVersions(demandVersions, resultDemand, result.project);
      const isHistoricalDemandUpdate = Boolean(
        payload.action === "update_current" &&
          payload.baseDemandId &&
          currentDemand?.id &&
          payload.baseDemandId !== currentDemand.id,
      );
      const nextEffectiveContext =
        !isNewProject &&
        (workbench?.effectiveSource === "contract_handover" || isHistoricalDemandUpdate)
          ? {
              effectiveDemandId: workbench?.effectiveDemandId,
              effectiveItineraryId: workbench?.effectiveItineraryId,
              effectiveSource: workbench?.effectiveSource ?? "latest_demand",
            }
          : {
              effectiveDemandId: resultDemand.id,
              effectiveItineraryId: undefined,
              effectiveSource: "latest_demand" as const,
            };
      if (shouldOpenNewCustomization) {
        queryClient.setQueryData<CustomizationWorkbench>(
          [...customizationWorkbenchQueryRootKey, result.project.id],
          {
            ...nextEffectiveContext,
            project: result.project,
            currentDemand: resultDemand,
            demandVersions: nextDemandVersions,
            traces: [],
            projectFieldOptions: workbench?.projectFieldOptions,
            demandFieldOptions: workbench?.demandFieldOptions,
            opportunityBinding: result.opportunityBinding,
          },
        );
        setLocalOpportunityBinding(result.opportunityBinding);
        setSelectedTraceIds([]);
        setSelectedReferenceDemandId(null);
        setManualDraftPatch(null);
        setManualDraftBaseDemandId(null);
        options.onNewCustomizationCreated?.(result.project.id);
        return;
      }
      setLocalProject(result.project);
      setLocalDemand(isHistoricalDemandUpdate ? currentDemand : resultDemand);
      setLocalDemandVersions(nextDemandVersions);
      setViewingDemand(isHistoricalDemandUpdate ? resultDemand : null);
      setManualDraftPatch(null);
      setManualDraftBaseDemandId(null);
      setLocalTraces(isNewProject ? result.selectedTraces : null);
      setSelectedTraceIds([]);
      setSelectedReferenceDemandId(null);
      queryClient.setQueryData<CustomizationWorkbench>(
        [...customizationWorkbenchQueryRootKey, result.project.id],
        {
          ...nextEffectiveContext,
          project: result.project,
          currentDemand: isHistoricalDemandUpdate && currentDemand ? currentDemand : resultDemand,
          demandVersions: nextDemandVersions,
          traces: isNewProject ? result.selectedTraces : traces,
          projectFieldOptions: workbench?.projectFieldOptions,
          demandFieldOptions: workbench?.demandFieldOptions,
          opportunityBinding: result.opportunityBinding,
        },
      );
      setLocalOpportunityBinding(result.opportunityBinding);
      if (!isNewProject && !isHistoricalDemandUpdate) {
        void refreshWorkbenchAfterDemandSuccess({
          projectId: resolvedProjectId,
          opportunityId,
          resultProject: result.project,
          resultDemand,
          nextDemandVersions,
          queryClient,
          setLocalProject,
          setLocalDemand,
          setLocalDemandVersions,
          setLocalTraces,
          setLocalOpportunityBinding,
        });
      }
      if (isNewProject) {
        options.onProjectChanged?.(result.project.id);
      }
    },
  });

  const itineraryGenerationMutation = useMutation({
    mutationFn: async (payload: { demandId: string; orderVersionKey: string }) => {
      const validation = await validateCustomizationItineraryGeneration(
        resolvedProjectId,
        payload.demandId,
      );
      if (!validation.valid) {
        throw new ItineraryGenerationBlockedError(
          validation.reasons.length
            ? validation.reasons.join("；")
            : "出发高校/组织、客户、客户联系人不匹配，暂不能生成行程。",
        );
      }
      await startCustomizationItineraryGeneration(
        resolvedProjectId,
        payload.demandId,
        payload.orderVersionKey,
      );
      return payload;
    },
  });
  const dispatcherNotificationMutation = useMutation({
    mutationFn: (payload: {
      demandId: string;
      source: "mobile_customization" | "pc_itinerary";
      actionText?: string;
    }) =>
      notifyCustomizationDispatcher(resolvedProjectId, {
        demandId: payload.demandId,
        source: payload.source,
        actionText: payload.actionText,
      }),
  });
  const displayOrderVersionKey = buildCustomizationOrderVersionKey(project, displayDemand);

  const ensureOpportunityBindingResolved = (
    binding: CustomizationOpportunityBinding | null = opportunityBinding,
  ) => {
    if (
      binding?.requiredComplete &&
      ["binding_required", "review_required", "conflict"].includes(binding.status)
    ) {
      dismissedOpportunityBindingKeyRef.current = "";
      setOpportunityBindingOpen(true);
      return false;
    }
    setOpportunityBindingOpen(false);
    dismissedOpportunityBindingKeyRef.current = "";
    return true;
  };

  const refreshOpportunityBindingAfterGateError = async (error: unknown) => {
    if (!isOpportunityBindingGateError(error)) return;
    try {
      const refreshed = await loadWorkbench(resolvedProjectId);
      setLocalProject(refreshed.project);
      setLocalDemand(refreshed.currentDemand);
      setLocalDemandVersions(refreshed.demandVersions);
      setLocalTraces(refreshed.traces);
      setLocalOpportunityBinding(refreshed.opportunityBinding);
      queryClient.setQueryData<CustomizationWorkbench>(
        [...customizationWorkbenchQueryRootKey, resolvedProjectId, opportunityId ?? null],
        refreshed,
      );
    } finally {
      dismissedOpportunityBindingKeyRef.current = "";
      setOpportunityBindingOpen(true);
    }
  };

  const previewReferenceDemandFromContent = async (content: string, requestId?: number) => {
    const referenceKey = extractReferenceKey(content);
    if (!referenceKey) {
      clearReferencePreviewState();
      return;
    }
    setReferenceErrorMessage("");
    try {
      const items = await fetchReferenceDemandItems(resolvedProjectId, referenceKey);
      if (requestId !== undefined && routeLinkPreviewRequestRef.current !== requestId) {
        return;
      }
      setReferenceCandidates(items);
      setSelectedReferenceDemandId((current) =>
        current && items.some((item) => item.demandId === current)
          ? current
          : items[0]?.demandId ?? null,
      );
    } catch {
      if (requestId !== undefined && routeLinkPreviewRequestRef.current !== requestId) {
        return;
      }
      setReferenceCandidates([]);
      setSelectedReferenceDemandId(null);
      setReferenceErrorMessage("参考定制预览加载失败，请稍后重试。");
    }
  };

  /** 清空参考定制预览状态，仅在已有状态时提交更新。 */
  function clearReferencePreviewState() {
    if (referenceCandidates.length > 0) {
      setReferenceCandidates([]);
    }
    if (selectedReferenceDemandId) {
      setSelectedReferenceDemandId(null);
    }
    if (referenceErrorMessage) {
      setReferenceErrorMessage("");
    }
  }

  /** 清空行程链接预览状态，仅在已有状态时提交更新。 */
  function clearRouteLinkPreviewState() {
    if (routeLinkPreview) {
      setRouteLinkPreview(null);
    }
    if (routeLinkPreviewing) {
      setRouteLinkPreviewing(false);
    }
    if (routeLinkErrorMessage) {
      setRouteLinkErrorMessage("");
    }
  }

  return {
    project,
    persistedProject,
    quickItinerary,
    currentDemand,
    displayDemand,
    persistedDisplayDemand: baseDisplayDemand,
    itineraryEditorTarget,
    demandVersions,
    recentDemandVersions: demandVersions.slice(0, 3),
    displayOrderVersionKey,
    traces,
    projectFieldOptions: workbench?.projectFieldOptions,
    demandFieldOptions: workbench?.demandFieldOptions,
    selectedTraceIds: selectableSelectedTraceIds,
    selectedTraces,
    loading: query.isLoading || isLegacyTargetVersionPending,
    errorMessage: query.error ? "需求定制工作台加载失败，请稍后重试。" : "",
    mutationErrorMessage,
    opportunityBinding,
    opportunityBindingOpen,
    opportunityBindingPreparing,
    referenceErrorMessage,
    routeLinkErrorMessage,
    referenceCandidates,
    routeLinkPreview,
    routeLinkPreviewing,
    traceReferenceCandidatesByTraceId,
    traceRouteLinkPreviewByTraceId,
    selectedReferenceDemandId,
    selectedReferenceDemand,
    historicalGenerationBase,
    hasManualDraft: isManualDraftActive,
    manualDraftPatch: isManualDraftActive ? manualDraftPatch : null,
    manualDraftFieldCount,
    isDemandVersionSwitching:
      Boolean(switchingDemandVersionId) ||
      Boolean(legacyVersionLoadingId) ||
      isLegacyTargetVersionPending,
    generating: opportunityGateRefreshing || demandMutation.isPending,
    itineraryGenerating: itineraryGenerationMutation.isPending,
    dispatcherNotifying: dispatcherNotificationMutation.isPending,
    traceSubmitting: traceMutation.isPending,
    projectSaving: false,
    refreshWorkbench: refreshWorkbenchVersionState,
    closeOpportunityBinding() {
      if (opportunityBinding) {
        dismissedOpportunityBindingKeyRef.current =
          buildOpportunityBindingPresentationKey(opportunityBinding);
      }
      setOpportunityBindingOpen(false);
    },
    async resolveOpportunityBinding(decision: CustomizationOpportunityBindingDecision) {
      try {
        const resolved = await resolveCustomizationOpportunityBinding(
          resolvedProjectId,
          decision,
        );
        setMutationErrorMessage("");
        try {
          const refreshed = await loadWorkbench(resolvedProjectId);
          setLocalProject(refreshed.project);
          setLocalDemand(refreshed.currentDemand);
          setLocalDemandVersions(refreshed.demandVersions);
          setLocalTraces(refreshed.traces);
          setLocalOpportunityBinding(refreshed.opportunityBinding);
          queryClient.setQueryData<CustomizationWorkbench>(
            [...customizationWorkbenchQueryRootKey, resolvedProjectId, opportunityId ?? null],
            refreshed,
          );
          setOpportunityBindingOpen(false);
          dismissedOpportunityBindingKeyRef.current = "";
          return refreshed.opportunityBinding;
        } catch {
          setLocalOpportunityBinding(resolved);
          const refreshMessage = "商机归属已保存，但刷新工作台失败，请手动刷新后确认状态。";
          setMutationErrorMessage(refreshMessage);
          setOpportunityBindingOpen(true);
          throw new Error(refreshMessage);
        }
      } catch (error) {
        if (error instanceof Error && error.message === "商机归属已保存，但刷新工作台失败，请手动刷新后确认状态。") {
          throw error;
        }
        const message = resolveCustomizationMutationErrorMessage(error);
        // 归属选择器内已用 Message + Alert 展示失败；避免再弹底层“操作失败”对话框被归属 Modal 挡住。
        if (
          isHttpClientError(error) &&
          error.code === "CUSTOMIZATION_OPPORTUNITY_STALE"
        ) {
          try {
            const refreshed = await loadWorkbench(resolvedProjectId);
            setLocalProject(refreshed.project);
            setLocalDemand(refreshed.currentDemand);
            setLocalDemandVersions(refreshed.demandVersions);
            setLocalTraces(refreshed.traces);
            setLocalOpportunityBinding(refreshed.opportunityBinding);
            queryClient.setQueryData<CustomizationWorkbench>(
              [...customizationWorkbenchQueryRootKey, resolvedProjectId, opportunityId ?? null],
              refreshed,
            );
            dismissedOpportunityBindingKeyRef.current = "";
            setOpportunityBindingOpen(true);
          } catch {
            setOpportunityBindingOpen(true);
          }
        } else {
          setOpportunityBindingOpen(true);
        }
        throw new Error(message, { cause: error });
      }
    },
    setSelectedTraceIds(traceIds: string[]) {
      setSelectedTraceIds(
        traceIds.filter(
          (traceId, index, all) =>
            selectableTraceIds.has(traceId) && all.indexOf(traceId) === index,
        ),
      );
    },
    toggleTrace(traceId: string) {
      if (!selectableTraceIds.has(traceId)) {
        setSelectedTraceIds((current) => current.filter((id) => id !== traceId));
        return;
      }
      setSelectedTraceIds((current) =>
        current.includes(traceId)
          ? current.filter((id) => id !== traceId)
          : [...current, traceId],
      );
    },
    async submitTrace(payload: CustomizationTraceCreate) {
      await runMutation(
        () => traceMutation.mutateAsync(payload),
        setMutationErrorMessage,
      );
    },
    async saveProject(payload: CustomizationProjectUpdatePayload, saveOptions: ProjectSaveOptions = {}) {
      void saveOptions;
      if (!project || !displayDemand?.id) {
        return null;
      }
      const baseDemandId = displayDemand.id;
      const nextPatch = mergeProjectDraftPayload(
        manualDraftPatch && manualDraftBaseDemandId === baseDemandId ? manualDraftPatch : {},
        payload,
      );
      const hasManualChanges = Object.keys(nextPatch).length > 0;
      setManualDraftBaseDemandId(hasManualChanges ? baseDemandId : null);
      setManualDraftPatch(hasManualChanges ? nextPatch : null);
      setMutationErrorMessage("");
      return applyProjectPayloadToSnapshot(persistedProject ?? project, nextPatch);
    },
    async loadProjectEditOptions(params?: {
      keyword?: string;
      primarySchoolId?: string;
      customerId?: string;
    }) {
      try {
        return await fetchCustomizationProjectEditOptions(resolvedProjectId, params);
      } catch {
        return null;
      }
    },
    async loadProjectPointNames(params?: {
      keyword?: string;
      stayAreaNames?: string[];
    }) {
      try {
        const result = await fetchCustomizationProjectPointNames(resolvedProjectId, params);
        return result.pointNames;
      } catch {
        return [];
      }
    },
    async previewTraceInput(content: string) {
      const referenceKey = extractReferenceKey(content);
      routeLinkPreviewRequestRef.current += 1;
      const requestId = routeLinkPreviewRequestRef.current;

      if (referenceKey) {
        clearRouteLinkPreviewState();
        await previewReferenceDemandFromContent(content, requestId);
        return;
      }

      clearReferencePreviewState();

      const normalizedContent = content.trim();
      if (!normalizedContent) {
        clearRouteLinkPreviewState();
        return;
      }
      if (!hasLegacyRouteLinkHint(normalizedContent)) {
        clearRouteLinkPreviewState();
        return;
      }

      setRouteLinkPreviewing(true);
      setRouteLinkErrorMessage("");
      try {
        const result = await previewCustomizationRouteLink(resolvedProjectId, normalizedContent);
        if (routeLinkPreviewRequestRef.current !== requestId) {
          return;
        }
        setRouteLinkPreview(result.recognized && result.route ? result : null);
      } catch {
        if (routeLinkPreviewRequestRef.current !== requestId) {
          return;
        }
        setRouteLinkPreview(null);
        setRouteLinkErrorMessage("行程预览加载失败，请稍后重试。");
      } finally {
        if (routeLinkPreviewRequestRef.current === requestId) {
          setRouteLinkPreviewing(false);
        }
      }
    },
    async previewReferenceDemand(content: string) {
      routeLinkPreviewRequestRef.current += 1;
      await previewReferenceDemandFromContent(content, routeLinkPreviewRequestRef.current);
    },
    toggleReferenceDemand(demandId: string) {
      setSelectedReferenceDemandId((current) => (current === demandId ? null : demandId));
      setMutationErrorMessage("");
    },
    async selectDemandVersion(demandId: string) {
      if (demandId === displayDemand?.id) {
        setMutationErrorMessage("");
        return;
      }
      setManualDraftPatch(null);
      setManualDraftBaseDemandId(null);
      const isCurrentDemandSelection = demandId === currentDemand?.id;
      const switchStartedAt = Date.now();
      setSwitchingDemandVersionId(demandId);
      void refreshWorkbenchVersionState(demandId);
      try {
        if (isCurrentDemandSelection) {
          setViewingDemand(null);
          setMutationErrorMessage("");
          return;
        }
        const demand = await runMutation(
          () => fetchCustomizationDemand(resolvedProjectId, demandId, { view: "workbench" }),
          setMutationErrorMessage,
        );
        if (demand) {
          setLocalDemandVersions((current) =>
            mergeDemandVersionRequiredStatuses(current ?? demandVersions, [demand]),
          );
          setViewingDemand(demand);
        }
      } finally {
        const elapsedMs = Date.now() - switchStartedAt;
        if (elapsedMs < DEMAND_VERSION_SWITCH_VISIBLE_MS) {
          await new Promise((resolve) =>
            window.setTimeout(resolve, DEMAND_VERSION_SWITCH_VISIBLE_MS - elapsedMs),
          );
        }
        setSwitchingDemandVersionId((current) => (current === demandId ? null : current));
      }
    },
    async generateDemandVersion(
      action: CreateCustomizationDemandPayload["action"],
      generationOptions?: { baseDemandId?: string },
    ): Promise<CreateCustomizationDemandResult | null> {
      if (displayDemand?.id && !(await quickItinerary.save())) return null;
      const binding =
        action === "create_version"
          ? await refreshWorkbenchForOpportunityGate()
          : opportunityBinding;
      if (action === "create_version" && binding === undefined) {
        return null;
      }
      if (action === "create_version" && !ensureOpportunityBindingResolved(binding)) {
        return null;
      }
      const activeManualPatch =
        manualDraftPatch && manualDraftBaseDemandId && manualDraftBaseDemandId === displayDemand?.id
          ? manualDraftPatch
          : undefined;
      const resolvedBaseDemandId =
        generationOptions?.baseDemandId ??
        (action === "update_current" && displayDemand?.id && displayDemand.id !== currentDemand?.id
          ? displayDemand.id
          : undefined);
      return runMutation(
        () =>
          demandMutation.mutateAsync({
            action,
            selectedTraceIds: selectableSelectedTraceIds,
            templateDemandId: selectedReferenceDemandId ?? undefined,
            baseDemandId: resolvedBaseDemandId,
            manualPatch: activeManualPatch,
          }),
        setMutationErrorMessage,
        refreshOpportunityBindingAfterGateError,
      );
    },
    async createNewCustomization() {
      if (!(await quickItinerary.save())) return null;
      if (!displayDemand?.id) {
        return;
      }
      const activeManualPatch =
        manualDraftPatch && manualDraftBaseDemandId === displayDemand.id
          ? manualDraftPatch
          : undefined;
      return runMutation(
        () =>
          demandMutation.mutateAsync({
            action: "create_new",
            selectedTraceIds: selectableSelectedTraceIds,
            baseDemandId: displayDemand.id,
            manualPatch: activeManualPatch,
          }),
        setMutationErrorMessage,
      );
    },
    async startItineraryGeneration() {
      if (!(await quickItinerary.save())) return false;
      if (
        isManualDraftActive ||
        !displayOrderVersionKey ||
        !displayDemand?.id ||
        getDemandRequiredStatus(displayDemand).status !== "complete"
      ) {
        return false;
      }
      const displayDemandId = displayDemand.id;
      const generated = await runMutation(
        () =>
          itineraryGenerationMutation.mutateAsync({
            demandId: displayDemandId,
            orderVersionKey: displayOrderVersionKey,
          }),
        setMutationErrorMessage,
        refreshOpportunityBindingAfterGateError,
      );
      if (!generated) {
        return false;
      }
      const nextTarget: ItineraryEditorTarget = { type: "generate", demandId: displayDemandId };
      setItineraryEditorTargetsByDemandId((current) => ({
        ...current,
        [displayDemandId]: nextTarget,
      }));
      setItineraryEditorTarget(nextTarget);
      return true;
    },
    startManualItineraryCreation() {
      if (!displayDemand?.id) {
        return false;
      }
      // PC 工作台改为独立页跳转；此处仅校验当前需求可用，不再挂载嵌入编辑器。
      return true;
    },
    async notifyDispatcher(
      source: "mobile_customization" | "pc_itinerary" = "pc_itinerary",
      actionText?: string,
    ) {
      if (isManualDraftActive || !displayDemand?.id) {
        return null;
      }
      if (!ensureOpportunityBindingResolved()) {
        return null;
      }
      return runMutation(
        () =>
          dispatcherNotificationMutation.mutateAsync({
            demandId: displayDemand.id!,
            source,
            actionText,
          }),
        setMutationErrorMessage,
        refreshOpportunityBindingAfterGateError,
      );
    },
  };
}

/** 比较两个版本级行程入口是否指向同一个旧行程或生成入口，避免重复写入状态。 */
function isSameItineraryEditorTarget(
  left: ItineraryEditorTarget | null | undefined,
  right: ItineraryEditorTarget | null | undefined,
): boolean {
  if (!left && !right) {
    return true;
  }
  if (!left || !right || left.type !== right.type) {
    return false;
  }
  return left.demandId === right.demandId;
}

/** 用已加载详情中的必填项校验结果修正版本列表，避免版本按钮和详情状态不一致。 */
function mergeDemandVersionRequiredStatuses(
  currentVersions: CustomizationDemandVersionOption[],
  demands: Array<CustomizationDemandDraft | null | undefined>,
): CustomizationDemandVersionOption[] {
  const reasonsByDemandId = new Map<string, string[]>();
  for (const demand of demands) {
    if (!demand?.id || !Array.isArray(demand.needsHumanReviewReasons)) {
      continue;
    }
    reasonsByDemandId.set(demand.id, demand.needsHumanReviewReasons);
  }
  if (reasonsByDemandId.size === 0) {
    return currentVersions;
  }

  let changed = false;
  const nextVersions = currentVersions.map((version) => {
    if (!reasonsByDemandId.has(version.id)) {
      return version;
    }
    const nextReasons = reasonsByDemandId.get(version.id) ?? [];
    if (
      Array.isArray(version.needsHumanReviewReasons) &&
      areStringArraysEqual(version.needsHumanReviewReasons, nextReasons)
    ) {
      return version;
    }
    changed = true;
    return {
      ...version,
      needsHumanReviewReasons: nextReasons,
    };
  });

  return changed ? nextVersions : currentVersions;
}

/** 合并刷新后的版本行程状态，同时保留本地已加载详情修正过的必填状态。 */
function mergeRefreshedDemandVersionOptions(
  refreshedVersions: CustomizationDemandVersionOption[],
  currentVersions: CustomizationDemandVersionOption[],
): CustomizationDemandVersionOption[] {
  const currentById = new Map(currentVersions.map((version) => [version.id, version]));
  return refreshedVersions.map((version) => {
    const current = currentById.get(version.id);
    if (!current?.needsHumanReviewReasons) {
      return version;
    }
    return {
      ...version,
      needsHumanReviewReasons: current.needsHumanReviewReasons,
    };
  });
}

/** 获取当前项目最新的需求版本选项。 */
function getLatestDemandVersion(
  versions: CustomizationDemandVersionOption[],
): CustomizationDemandVersionOption | null {
  if (versions.length === 0) {
    return null;
  }
  return versions.reduce((latest, version) =>
    version.versionSeq > latest.versionSeq ? version : latest,
  );
}

/** 判断两个字符串数组是否完全一致。 */
function areStringArraysEqual(left: string[], right: string[]): boolean {
  return left.length === right.length && left.every((item, index) => item === right[index]);
}

/** 拼出调用行程生成所需的订单版本号，例如 JD202605250005_2。 */
function buildCustomizationOrderVersionKey(
  project: CustomizationProjectSnapshot | null,
  demand: CustomizationDemandDraft | null,
): string {
  const orderCode = project?.legacyId?.trim() || project?.projectCode?.trim() || "";
  if (!orderCode || !demand?.versionSeq) {
    return "";
  }
  return `${orderCode}_${demand.versionSeq}`;
}

/** 从旧系统 JiDiaoNo 中提取版本序号，例如 JD202512040008_6 提取为 6。*/
function parseLegacyJiDiaoVersionSeq(value?: string): number | null {
  return parseLegacyJdReference(value)?.versionSeq ?? null;
}

/** 根据旧系统 JiDiaoNo 找到追溯页或旧入口需要展示的目标需求版本。 */
function resolveLegacyDemandVersion(
  legacyJiDiaoNo: string | undefined,
  demandVersions: CustomizationDemandVersionOption[],
  currentDemand: CustomizationDemandDraft | null,
): CustomizationDemandVersionOption | null {
  if (demandVersions.length === 0) {
    return null;
  }
  const targetVersionSeq = legacyJiDiaoNo
    ? parseLegacyJiDiaoVersionSeq(legacyJiDiaoNo)
    : null;
  if (targetVersionSeq === null) {
    return currentDemand ? null : demandVersions[0] ?? null;
  }
  return (
    demandVersions.find((version) => version.versionSeq === targetVersionSeq) ??
    (currentDemand ? null : demandVersions[0] ?? null)
  );
}

/** 合并新生成的需求版本，等待工作台刷新前先保持页面一致。 */
function mergeDemandVersions(
  currentVersions: CustomizationDemandVersionOption[],
  demand: CustomizationDemandDraft,
  project: CustomizationProjectSnapshot | null,
): CustomizationDemandVersionOption[] {
  if (!demand.id || !demand.versionNumber || !demand.versionSeq) {
    return currentVersions;
  }
  const nextVersion = {
    id: demand.id,
    versionNumber: demand.versionNumber,
    versionSeq: demand.versionSeq,
    createdAt: demand.createdAt ?? "",
    isCurrent: true,
    orderVersionKey: buildCustomizationOrderVersionKey(project, demand),
    hasItinerary: false,
    itineraryCount: 0,
    needsHumanReviewReasons: demand.needsHumanReviewReasons ?? [],
  };
  return [
    nextVersion,
    ...currentVersions
      .filter((version) => version.id !== demand.id)
      .map((version) => ({ ...version, isCurrent: false })),
  ].sort((left, right) => right.versionSeq - left.versionSeq);
}

/** 判断追溯参考预览缓存是否只包含当前需要的追溯。 */
function areTraceReferenceIdsEqual(
  current: Record<string, CustomizationReferenceDemandCandidate[]>,
  expectedIds: Set<string>,
): boolean {
  const currentIds = Object.keys(current);
  return (
    currentIds.length === expectedIds.size &&
    currentIds.every((traceId) => expectedIds.has(traceId))
  );
}

/** 判断追溯行程预览缓存是否只包含当前需要展示的追溯。 */
function areTraceRouteLinkPreviewIdsEqual(
  current: Record<string, CustomizationRouteLinkRecognitionResult>,
  expectedIds: Set<string>,
): boolean {
  const currentIds = Object.keys(current);
  return (
    currentIds.length === expectedIds.size &&
    currentIds.every((traceId) => expectedIds.has(traceId))
  );
}

/** 判断文本里是否包含本期支持的旧系统行程直链。 */
function hasLegacyRouteLinkHint(content: string): boolean {
  return LEGACY_ROUTE_LINK_HINT_PATTERN.test(content);
}

/** 合并多次本地编辑草稿。 */
function mergeProjectDraftPayload(
  current: CustomizationProjectUpdatePayload,
  patch: CustomizationProjectUpdatePayload,
): CustomizationProjectUpdatePayload {
  const result: CustomizationProjectUpdatePayload = {
    ...current,
    ...patch,
  };
  for (const key of Object.keys(result) as Array<keyof CustomizationProjectUpdatePayload>) {
    if (result[key] === undefined) {
      delete result[key];
    }
  }
  return result;
}

/** 将本地草稿叠加到当前展示需求，并生成提交前预览红标。 */
function applyManualDraftToDemand(
  demand: CustomizationDemandDraft,
  payload: CustomizationProjectUpdatePayload,
  project: CustomizationProjectSnapshot,
  baselineProject: CustomizationProjectSnapshot | null,
): CustomizationDemandDraft {
  const nextDemand = applyProjectPayloadToDemand(demand, payload, project) ?? demand;
  return {
    ...nextDemand,
    needsHumanReviewReasons: resolveManualDraftRequiredReasons({
      demand: nextDemand,
      project,
      currentReasons: demand.needsHumanReviewReasons ?? [],
    }),
    changedFields: {
      ...(demand.changedFields ?? {}),
      ...buildManualDraftChangedFields({
        baselineDemand: demand,
        baselineProject,
        nextDemand,
        nextProject: project,
        payload,
      }),
    },
  };
}

/** 基于本地草稿重新计算必填项缺失原因，并保留非必填复核原因。 */
function resolveManualDraftRequiredReasons({
  demand,
  project,
  currentReasons,
}: {
  demand: CustomizationDemandDraft;
  project: CustomizationProjectSnapshot;
  currentReasons: string[];
}): string[] {
  const missingLabels = getActualDemandRequiredMissingFields(demand, project).map(
    (label) => `缺少${label}`,
  );
  const preservedReviewReasons = normalizeDemandReviewReasons(currentReasons).filter(
    (reason) => !reason.includes("缺少") && !reason.includes("待补"),
  );
  return mergeUniqueStrings(preservedReviewReasons, missingLabels);
}

/** 合并多组文案并保持出现顺序去重。 */
function mergeUniqueStrings(...groups: string[][]): string[] {
  const result: string[] = [];
  const seen = new Set<string>();
  for (const group of groups) {
    for (const rawValue of group) {
      const value = rawValue.trim();
      if (!value || seen.has(value)) {
        continue;
      }
      seen.add(value);
      result.push(value);
    }
  }
  return result;
}

/** 计算本地草稿相对基线版本的字段变更。 */
function buildManualDraftChangedFields({
  baselineDemand,
  baselineProject,
  nextDemand,
  nextProject,
  payload,
}: {
  baselineDemand: CustomizationDemandDraft;
  baselineProject: CustomizationProjectSnapshot | null;
  nextDemand: CustomizationDemandDraft;
  nextProject: CustomizationProjectSnapshot;
  payload: CustomizationProjectUpdatePayload;
}): NonNullable<CustomizationDemandDraft["changedFields"]> {
  const changes: NonNullable<CustomizationDemandDraft["changedFields"]> = {};
  const addChange = (payloadKey: keyof CustomizationProjectUpdatePayload, fieldKey: string, previous: unknown, current: unknown) => {
    if (payload[payloadKey] === undefined || isDraftValueEqual(previous, current)) {
      return;
    }
    changes[fieldKey] = {
      previous,
      current,
      traceRecordIds: [],
      reason: "本地手工草稿字段发生变化",
    };
  };

  addChange("primarySchoolName", "primarySchoolNameSnapshot", baselineProject?.primarySchoolNameSnapshot, nextProject.primarySchoolNameSnapshot);
  addChange("customerName", "customerName", baselineProject?.customerName, nextProject.customerName);
  addChange("contactName", "contactName", baselineProject?.contactName, nextProject.contactName);
  addChange("dispatcherUserName", "dispatcherUserName", baselineProject?.dispatcherUserName, nextProject.dispatcherUserName);
  if (
    !changes.dispatcherUserName &&
    payload.dispatcherUserId !== undefined &&
    !isDraftValueEqual(baselineProject?.dispatcherUserId, nextProject.dispatcherUserId)
  ) {
    changes.dispatcherUserName = {
      previous: baselineProject?.dispatcherUserName,
      current: nextProject.dispatcherUserName ?? nextProject.dispatcherUserId,
      traceRecordIds: [],
      reason: "本地手工草稿字段发生变化",
    };
  }
  addChange("customerType", "customerType", baselineProject?.customerType, nextProject.customerType);
  addChange("tripType", "tripType", baselineProject?.tripType, nextProject.tripType);
  addChange("signType", "signType", baselineProject?.signType, nextProject.signType);
  addChange("dealProgress", "dealProgress", baselineProject?.dealProgress, nextProject.dealProgress);
  addChange("operatingCompany", "operatingCompany", baselineProject?.operatingCompany, nextProject.operatingCompany);

  addChange("provinces", "provinces", baselineDemand.relations?.provinces, nextDemand.relations?.provinces);
  addChange("majorNames", "majorNames", baselineDemand.relations?.majorNames, nextDemand.relations?.majorNames);
  addChange("pointNames", "pointNames", baselineDemand.relations?.pointNames, nextDemand.relations?.pointNames);
  addChange("stayAreaNames", "stayAreaNames", baselineDemand.relations?.stayAreaNames, nextDemand.relations?.stayAreaNames);

  addChange("departureStartDate", "departureStartDate", baselineDemand.departureStartDate, nextDemand.departureStartDate);
  addChange("durationDays", "durationDays", baselineDemand.durationDays, nextDemand.durationDays);
  addChange("studentCount", "studentCount", baselineDemand.studentCount, nextDemand.studentCount);
  addChange("teacherCount", "teacherCount", baselineDemand.teacherCount, nextDemand.teacherCount);
  addChange("totalCount", "totalCount", baselineDemand.totalCount, nextDemand.totalCount);
  addChange("budgetPerPerson", "budgetPerPerson", baselineDemand.budgetPerPerson, nextDemand.budgetPerPerson);
  addChange("teachingRequirements", "teachingRequirements", baselineDemand.teachingRequirements, nextDemand.teachingRequirements);
  addChange("specialRequirements", "specialRequirements", baselineDemand.specialRequirements, nextDemand.specialRequirements);
  addChange("schoolPickupRequirement", "schoolPickupRequirement", baselineDemand.schoolPickupRequirement, nextDemand.schoolPickupRequirement);
  addChange("invoiceRequirement", "invoiceRequirement", baselineDemand.invoiceRequirement, nextDemand.invoiceRequirement);
  addChange("teacherMealRequirement", "teacherMealRequirement", baselineDemand.teacherMealRequirement, nextDemand.teacherMealRequirement);
  addChange("teacherTicketRequirement", "teacherTicketRequirement", baselineDemand.teacherTicketRequirement, nextDemand.teacherTicketRequirement);
  addChange("teacherTransportRequirement", "teacherTransportRequirement", baselineDemand.teacherTransportRequirement, nextDemand.teacherTransportRequirement);
  addChange("teacherAccommodationRequirement", "teacherAccommodationRequirement", baselineDemand.teacherAccommodationRequirement, nextDemand.teacherAccommodationRequirement);
  addChange("remark", "remark", baselineDemand.remark, nextDemand.remark);

  addChange("roundTripTransportNote", "roundTripTransportNote", baselineDemand.serviceStandard?.roundTripTransportNote, nextDemand.serviceStandard?.roundTripTransportNote);
  addChange("localTransportNote", "localTransportNote", baselineDemand.serviceStandard?.localTransportNote, nextDemand.serviceStandard?.localTransportNote);
  addChange("accommodationNote", "accommodationNote", baselineDemand.serviceStandard?.accommodationNote, nextDemand.serviceStandard?.accommodationNote);
  addChange("guideCount", "guideCount", baselineDemand.serviceStandard?.guideCount, nextDemand.serviceStandard?.guideCount);
  addChange("guideNote", "guideNote", baselineDemand.serviceStandard?.guideNote, nextDemand.serviceStandard?.guideNote);
  addChange("includeTickets", "includeTickets", baselineDemand.serviceStandard?.includeTickets, nextDemand.serviceStandard?.includeTickets);
  addChange("ticketNote", "ticketNote", baselineDemand.serviceStandard?.ticketNote, nextDemand.serviceStandard?.ticketNote);
  addChange("includeMeals", "includeMeals", baselineDemand.serviceStandard?.includeMeals, nextDemand.serviceStandard?.includeMeals);
  addChange("mealStandards", "mealStandards", baselineDemand.serviceStandard?.mealStandards, nextDemand.serviceStandard?.mealStandards);
  addChange("mealNote", "mealNote", baselineDemand.serviceStandard?.mealNote, nextDemand.serviceStandard?.mealNote);
  addChange("companyVisitCount", "companyVisitCount", baselineDemand.serviceStandard?.companyVisitCount, nextDemand.serviceStandard?.companyVisitCount);
  addChange("companyVisitNote", "companyVisitNote", baselineDemand.serviceStandard?.companyVisitNote, nextDemand.serviceStandard?.companyVisitNote);
  addChange("intangibleHeritageCount", "intangibleHeritageCount", baselineDemand.serviceStandard?.intangibleHeritageCount, nextDemand.serviceStandard?.intangibleHeritageCount);
  addChange("intangibleHeritageNote", "intangibleHeritageNote", baselineDemand.serviceStandard?.intangibleHeritageNote, nextDemand.serviceStandard?.intangibleHeritageNote);
  return changes;
}

/** 比较草稿值，兼容数组和空值。 */
function isDraftValueEqual(left: unknown, right: unknown): boolean {
  if (Array.isArray(left) || Array.isArray(right)) {
    const leftItems = Array.isArray(left) ? left : [];
    const rightItems = Array.isArray(right) ? right : [];
    return leftItems.length === rightItems.length && leftItems.every((item, index) => item === rightItems[index]);
  }
  return (left ?? undefined) === (right ?? undefined);
}

/** 将项目保存 payload 合并进项目快照。 */
function applyProjectPayloadToSnapshot(
  project: CustomizationProjectSnapshot,
  payload: CustomizationProjectUpdatePayload,
): CustomizationProjectSnapshot {
  const schoolTouched = Object.prototype.hasOwnProperty.call(payload, "primarySchoolId");
  const nextSchoolId = schoolTouched ? payload.primarySchoolId : project.primarySchoolId;
  const nextOrgType = Object.prototype.hasOwnProperty.call(payload, "primarySchoolOrgType")
    ? payload.primarySchoolOrgType
    : schoolTouched
      ? undefined
      : project.primarySchoolOrgType;
  const readClearableText = (
    key: "customerId" | "customerName" | "contactId" | "contactName",
    fallback: string | undefined,
  ): string | undefined => {
    if (Object.prototype.hasOwnProperty.call(payload, key)) {
      const raw = payload[key];
      if (typeof raw !== "string") {
        return undefined;
      }
      const text = raw.trim();
      return text || undefined;
    }
    if (schoolTouched) {
      return undefined;
    }
    return fallback;
  };
  return {
    ...project,
    primarySchoolId: nextSchoolId,
    primarySchoolNameSnapshot: payload.primarySchoolName ?? project.primarySchoolNameSnapshot,
    primarySchoolOrgType: nextOrgType,
    customerId: readClearableText("customerId", project.customerId),
    customerName: readClearableText("customerName", project.customerName),
    contactId: readClearableText("contactId", project.contactId),
    contactName: readClearableText("contactName", project.contactName),
    customerType: payload.customerType ?? project.customerType,
    tripType: payload.tripType ?? project.tripType,
    signType: payload.signType ?? project.signType,
    dealProgress: payload.dealProgress ?? project.dealProgress,
    dispatcherUserId: payload.dispatcherUserId ?? project.dispatcherUserId,
    dispatcherUserName: payload.dispatcherUserName ?? project.dispatcherUserName,
    operatingCompany: payload.operatingCompany ?? project.operatingCompany,
  };
}

/** 将项目保存 payload 合并进当前需求草稿。 */
function applyProjectPayloadToDemand(
  demand: CustomizationDemandDraft | null,
  payload: CustomizationProjectUpdatePayload,
  project: CustomizationProjectSnapshot,
): CustomizationDemandDraft | null {
  if (!demand) {
    return demand;
  }
  const relations = applyProjectPayloadToDemandRelations(demand, payload);
  const summary = applyProjectPayloadToDemandSummary(demand, payload);
  const customerRequirements = applyProjectPayloadToDemandCustomerRequirements(demand, payload);
  const teacherRequirements = applyProjectPayloadToDemandTeacherRequirements(demand, payload);
  const serviceStandard = applyProjectPayloadToDemandServiceStandard(demand, payload);
  return {
    ...demand,
    project: {
      ...demand.project,
      ...project,
    },
    relations,
    summary,
    customerRequirements,
    teacherRequirements,
    serviceStandard,
    departureStartDate: payload.departureStartDate ?? demand.departureStartDate,
    durationDays: payload.durationDays ?? demand.durationDays,
    studentCount: payload.studentCount ?? demand.studentCount,
    teacherCount: payload.teacherCount ?? demand.teacherCount,
    totalCount: payload.totalCount ?? demand.totalCount,
    budgetPerPerson: payload.budgetPerPerson ?? demand.budgetPerPerson,
    teachingRequirements: payload.teachingRequirements ?? demand.teachingRequirements,
    specialRequirements: payload.specialRequirements ?? demand.specialRequirements,
    schoolPickupRequirement: payload.schoolPickupRequirement ?? demand.schoolPickupRequirement,
    invoiceRequirement: payload.invoiceRequirement ?? demand.invoiceRequirement,
    teacherMealRequirement: payload.teacherMealRequirement ?? demand.teacherMealRequirement,
    teacherTicketRequirement: payload.teacherTicketRequirement ?? demand.teacherTicketRequirement,
    teacherTransportRequirement: payload.teacherTransportRequirement ?? demand.teacherTransportRequirement,
    teacherAccommodationRequirement:
      payload.teacherAccommodationRequirement ?? demand.teacherAccommodationRequirement,
    remark: payload.remark ?? demand.remark,
    displayGroups: applyProjectPayloadToDisplayGroups(demand.displayGroups, payload),
  };
}

/** 将保存 payload 合并进需求关系字段。 */
function applyProjectPayloadToDemandRelations(
  demand: CustomizationDemandDraft,
  payload: CustomizationProjectUpdatePayload,
): CustomizationDemandDraft["relations"] {
  const hasRelationsPayload =
    payload.provinces !== undefined ||
    payload.majorNames !== undefined ||
    payload.pointNames !== undefined ||
    payload.stayAreaNames !== undefined;
  if (!hasRelationsPayload) {
    return demand.relations;
  }
  return {
    ...demand.relations,
    provinces: payload.provinces ?? demand.relations?.provinces,
    majorNames: payload.majorNames ?? demand.relations?.majorNames,
    pointNames: payload.pointNames ?? demand.relations?.pointNames,
    stayAreaNames: payload.stayAreaNames ?? demand.relations?.stayAreaNames,
  };
}

/** 将保存 payload 合并进需求摘要字段。 */
function applyProjectPayloadToDemandSummary(
  demand: CustomizationDemandDraft,
  payload: CustomizationProjectUpdatePayload,
): CustomizationDemandDraft["summary"] {
  const hasSummaryPayload =
    payload.departureStartDate !== undefined ||
    payload.durationDays !== undefined ||
    payload.studentCount !== undefined ||
    payload.teacherCount !== undefined ||
    payload.totalCount !== undefined ||
    payload.budgetPerPerson !== undefined;
  if (!hasSummaryPayload) {
    return demand.summary;
  }
  return {
    demandSummary: demand.summary?.demandSummary ?? demand.demandSummary,
    ...demand.summary,
    departureStartDate: payload.departureStartDate ?? demand.summary?.departureStartDate,
    durationDays: payload.durationDays ?? demand.summary?.durationDays,
    studentCount: payload.studentCount ?? demand.summary?.studentCount,
    teacherCount: payload.teacherCount ?? demand.summary?.teacherCount,
    totalCount: payload.totalCount ?? demand.summary?.totalCount,
    budgetPerPerson: payload.budgetPerPerson ?? demand.summary?.budgetPerPerson,
  };
}

/** 将保存 payload 合并进客户需求字段。 */
function applyProjectPayloadToDemandCustomerRequirements(
  demand: CustomizationDemandDraft,
  payload: CustomizationProjectUpdatePayload,
): CustomizationDemandDraft["customerRequirements"] {
  const hasCustomerRequirementPayload =
    payload.customerType !== undefined ||
    payload.tripType !== undefined ||
    payload.signType !== undefined ||
    payload.dealProgress !== undefined ||
    payload.operatingCompany !== undefined ||
    payload.teachingRequirements !== undefined ||
    payload.specialRequirements !== undefined ||
    payload.schoolPickupRequirement !== undefined ||
    payload.invoiceRequirement !== undefined ||
    payload.remark !== undefined;
  if (!hasCustomerRequirementPayload) {
    return demand.customerRequirements;
  }
  return {
    ...demand.customerRequirements,
    customerType: payload.customerType ?? demand.customerRequirements?.customerType,
    tripType: payload.tripType ?? demand.customerRequirements?.tripType,
    signType: payload.signType ?? demand.customerRequirements?.signType,
    dealProgress: payload.dealProgress ?? demand.customerRequirements?.dealProgress,
    operatingCompany: payload.operatingCompany ?? demand.customerRequirements?.operatingCompany,
    teachingRequirements:
      payload.teachingRequirements ?? demand.customerRequirements?.teachingRequirements,
    specialRequirements:
      payload.specialRequirements ?? demand.customerRequirements?.specialRequirements,
    schoolPickupRequirement:
      payload.schoolPickupRequirement ?? demand.customerRequirements?.schoolPickupRequirement,
    invoiceRequirement: payload.invoiceRequirement ?? demand.customerRequirements?.invoiceRequirement,
    remark: payload.remark ?? demand.customerRequirements?.remark,
  };
}

/** 将保存 payload 合并进老师需求字段。 */
function applyProjectPayloadToDemandTeacherRequirements(
  demand: CustomizationDemandDraft,
  payload: CustomizationProjectUpdatePayload,
): CustomizationDemandDraft["teacherRequirements"] {
  const hasTeacherRequirementPayload =
    payload.teacherMealRequirement !== undefined ||
    payload.teacherTicketRequirement !== undefined ||
    payload.teacherTransportRequirement !== undefined ||
    payload.teacherAccommodationRequirement !== undefined;
  if (!hasTeacherRequirementPayload) {
    return demand.teacherRequirements;
  }
  return {
    ...demand.teacherRequirements,
    teacherMealRequirement:
      payload.teacherMealRequirement ?? demand.teacherRequirements?.teacherMealRequirement,
    teacherTicketRequirement:
      payload.teacherTicketRequirement ?? demand.teacherRequirements?.teacherTicketRequirement,
    teacherTransportRequirement:
      payload.teacherTransportRequirement ?? demand.teacherRequirements?.teacherTransportRequirement,
    teacherAccommodationRequirement:
      payload.teacherAccommodationRequirement ?? demand.teacherRequirements?.teacherAccommodationRequirement,
  };
}

/** 将保存 payload 合并进服务标准字段。 */
function applyProjectPayloadToDemandServiceStandard(
  demand: CustomizationDemandDraft,
  payload: CustomizationProjectUpdatePayload,
): CustomizationDemandDraft["serviceStandard"] {
  const hasServicePayload =
    payload.roundTripTransportNote !== undefined ||
    payload.localTransportNote !== undefined ||
    payload.accommodationNote !== undefined ||
    payload.guideCount !== undefined ||
    payload.guideNote !== undefined ||
    payload.includeTickets !== undefined ||
    payload.ticketNote !== undefined ||
    payload.includeMeals !== undefined ||
    payload.mealStandards !== undefined ||
    payload.mealNote !== undefined ||
    payload.companyVisitCount !== undefined ||
    payload.companyVisitNote !== undefined ||
    payload.intangibleHeritageCount !== undefined ||
    payload.intangibleHeritageNote !== undefined;
  if (!hasServicePayload) {
    return demand.serviceStandard;
  }
  return {
    ...demand.serviceStandard,
    roundTripTransportNote:
      payload.roundTripTransportNote ?? demand.serviceStandard?.roundTripTransportNote,
    localTransportNote: payload.localTransportNote ?? demand.serviceStandard?.localTransportNote,
    accommodationNote: payload.accommodationNote ?? demand.serviceStandard?.accommodationNote,
    guideCount: payload.guideCount ?? demand.serviceStandard?.guideCount,
    guideNote: payload.guideNote ?? demand.serviceStandard?.guideNote,
    includeTickets: payload.includeTickets ?? demand.serviceStandard?.includeTickets,
    ticketNote: payload.ticketNote ?? demand.serviceStandard?.ticketNote,
    includeMeals: payload.includeMeals ?? demand.serviceStandard?.includeMeals,
    mealStandards: payload.mealStandards ?? demand.serviceStandard?.mealStandards,
    mealNote: payload.mealNote ?? demand.serviceStandard?.mealNote,
    companyVisitCount: payload.companyVisitCount ?? demand.serviceStandard?.companyVisitCount,
    companyVisitNote: payload.companyVisitNote ?? demand.serviceStandard?.companyVisitNote,
    intangibleHeritageCount:
      payload.intangibleHeritageCount ?? demand.serviceStandard?.intangibleHeritageCount,
    intangibleHeritageNote:
      payload.intangibleHeritageNote ?? demand.serviceStandard?.intangibleHeritageNote,
  };
}

/** 将保存 payload 合并进后端 displayGroups，避免刷新接口短暂返回旧展示值。 */
function applyProjectPayloadToDisplayGroups(
  groups: CustomizationDemandDraft["displayGroups"],
  payload: CustomizationProjectUpdatePayload,
): CustomizationDemandDraft["displayGroups"] {
  if (!groups?.length) {
    return groups;
  }
  return groups.map((group) => ({
    ...group,
    fields: group.fields.map((field) => applyProjectPayloadToDisplayField(field, payload)),
  }));
}

/** 将保存 payload 合并进单个展示字段。 */
function applyProjectPayloadToDisplayField(
  field: NonNullable<CustomizationDemandDraft["displayGroups"]>[number]["fields"][number],
  payload: CustomizationProjectUpdatePayload,
): NonNullable<CustomizationDemandDraft["displayGroups"]>[number]["fields"][number] {
  const value = resolveOptimisticDisplayFieldValue(field.key, payload);
  if (value === undefined) {
    return field;
  }
  const displayText = formatOptimisticDisplayValue(value);
  return {
    ...field,
    value: displayText,
    displayText,
  };
}

/** 根据字段 key 读取保存 payload 中对应的新值。 */
function resolveOptimisticDisplayFieldValue(
  fieldKey: string,
  payload: CustomizationProjectUpdatePayload,
): unknown {
  const key = normalizeOptimisticFieldKey(fieldKey);
  switch (key) {
    case "provinces":
      return payload.provinces;
    case "majorNames":
      return payload.majorNames;
    case "pointNames":
      return payload.pointNames;
    case "stayAreaNames":
      return payload.stayAreaNames;
    case "primarySchoolNameSnapshot":
      return payload.primarySchoolName;
    case "customerName":
      return payload.customerName;
    case "contactName":
      return payload.contactName;
    case "dispatcherUserName":
      return payload.dispatcherUserName;
    default:
      return (payload as Record<string, unknown>)[key];
  }
}

/** 规整展示字段 key。 */
function normalizeOptimisticFieldKey(fieldKey: string): string {
  const key = fieldKey.replace(/_([a-z])/g, (_, letter: string) => letter.toUpperCase());
  return key === "provinceNames" ? "provinces" : key;
}

/** 格式化保存 payload 值为需求卡片展示文本。 */
function formatOptimisticDisplayValue(value: unknown): string {
  if (Array.isArray(value)) {
    return value.map((item) => String(item).trim()).filter(Boolean).join(" / ") || "--";
  }
  if (typeof value === "boolean") {
    return value ? "是" : "否";
  }
  if (value === null || value === undefined || value === "") {
    return "--";
  }
  return String(value);
}

/** 判断追溯记录是否允许参与需求生成。 */
function isTraceSelectable(trace: CustomizationTraceItem): boolean {
  return trace.isSelectable !== false;
}

/** 从输入文本中提取首个 JD 参考定制号。 */
function extractReferenceKey(content: string): string | null {
  return findLegacyJdReferenceKey(content);
}

/** 查询参考 JD 候选；旧系统带版本号查不到时，退回基础 JD 兼容已同步数据。 */
async function fetchReferenceDemandItems(
  projectId: string,
  referenceKey: string,
): Promise<CustomizationReferenceDemandCandidate[]> {
  const result = await fetchCustomizationReferenceDemands(projectId, referenceKey);
  if (result.items.length > 0) {
    return result.items;
  }
  const legacyReference = parseLegacyJdReference(referenceKey);
  if (!legacyReference?.versionSeq) {
    return result.items;
  }
  const fallback = await fetchCustomizationReferenceDemands(projectId, legacyReference.batchNo);
  return fallback.items.map((item) => ({
    ...item,
    referenceKey,
  }));
}

/** 统一执行工作台变更请求并吞掉页内错误提示。 */
async function runMutation<T>(
  execute: () => Promise<T>,
  setMutationErrorMessage: (message: string) => void,
  onError?: (error: unknown) => void | Promise<void>,
): Promise<T | null> {
  setMutationErrorMessage("");
  try {
    return await execute();
  } catch (error) {
    await onError?.(error);
    setMutationErrorMessage(resolveCustomizationMutationErrorMessage(error));
    return null;
  }
}

/** 判断正式动作是否被服务端商机归属门禁阻断。 */
function isOpportunityBindingGateError(error: unknown): boolean {
  return Boolean(
    isHttpClientError(error) &&
      [
        "CUSTOMIZATION_OPPORTUNITY_BINDING_REQUIRED",
        "CUSTOMIZATION_OPPORTUNITY_REVIEW_REQUIRED",
        "CUSTOMIZATION_OPPORTUNITY_CONFLICT",
      ].includes(error.code ?? ""),
  );
}

/** 生成当前归属提示的展示键，供取消后同一状态暂时收起。 */
function buildOpportunityBindingPresentationKey(
  binding: CustomizationOpportunityBinding,
): string {
  return `${binding.status}:${binding.projectUpdatedAt ?? ""}:${binding.demandId ?? ""}`;
}

/** 生成接口成功后后台同步工作台详情，避免刷新慢时继续占用“版本处理中”。 */
async function refreshWorkbenchAfterDemandSuccess({
  projectId,
  opportunityId,
  resultProject,
  resultDemand,
  nextDemandVersions,
  queryClient,
  setLocalProject,
  setLocalDemand,
  setLocalDemandVersions,
  setLocalTraces,
  setLocalOpportunityBinding,
}: {
  projectId: string;
  opportunityId?: number;
  resultProject: CustomizationProjectSnapshot;
  resultDemand: CustomizationDemandDraft;
  nextDemandVersions: CustomizationDemandVersionOption[];
  queryClient: ReturnType<typeof useQueryClient>;
  setLocalProject: (project: CustomizationProjectSnapshot) => void;
  setLocalDemand: (demand: CustomizationDemandDraft | null) => void;
  setLocalDemandVersions: (versions: CustomizationDemandVersionOption[]) => void;
  setLocalTraces: (traces: CustomizationTraceItem[] | null) => void;
  setLocalOpportunityBinding: (binding: CustomizationOpportunityBinding) => void;
}): Promise<CustomizationOpportunityBinding | null> {
  try {
    const refreshed =
      opportunityId === undefined
        ? await fetchCustomizationWorkbench(projectId)
        : await fetchCustomizationWorkbench(projectId, { opportunityId });
    const shouldUseRefreshedDemand = refreshed.currentDemand?.id === resultDemand.id;
    const shouldUseRefreshedVersions = refreshed.demandVersions.some(
      (version) => version.id === resultDemand.id,
    );
    const mergedRefreshedVersions = shouldUseRefreshedVersions
      ? refreshed.demandVersions
      : nextDemandVersions;
    const nextProject = shouldUseRefreshedDemand ? refreshed.project : resultProject;
    const nextDemand = shouldUseRefreshedDemand ? refreshed.currentDemand : resultDemand;
    setLocalProject(nextProject);
    setLocalDemand(nextDemand);
    setLocalDemandVersions(mergedRefreshedVersions);
    setLocalTraces(refreshed.traces);
    setLocalOpportunityBinding(refreshed.opportunityBinding);
    queryClient.setQueryData<CustomizationWorkbench>(
      [...customizationWorkbenchQueryRootKey, projectId, opportunityId ?? null],
      {
        effectiveDemandId: refreshed.effectiveDemandId,
        effectiveItineraryId: refreshed.effectiveItineraryId,
        effectiveSource: refreshed.effectiveSource,
        project: nextProject,
        currentDemand: nextDemand,
        demandVersions: mergedRefreshedVersions,
        traces: refreshed.traces,
        projectFieldOptions: refreshed.projectFieldOptions,
        demandFieldOptions: refreshed.demandFieldOptions,
        opportunityBinding: refreshed.opportunityBinding,
      },
    );
    return refreshed.opportunityBinding;
  } catch {
    // 生成结果已经写入本地状态；后台刷新失败时保留当前可用结果，避免页面回到处理中。
    return null;
  }
}

/** 将需求定制变更错误转译为页面可读提示。 */
function resolveCustomizationMutationErrorMessage(error: unknown): string {
  if (error instanceof ItineraryGenerationBlockedError) {
    return error.message;
  }
  if (
    isHttpClientError(error) &&
    error.code === "CUSTOMIZATION_UPDATE_REQUIRES_CURRENT_DEMAND"
  ) {
    return "当前还没有可创建新版本的第一版，请先生成第一版。";
  }
  if (
    isHttpClientError(error) &&
    error.code === "CUSTOMIZATION_CURRENT_DEMAND_INCOMPLETE"
  ) {
    return "当前版本必填项还未补全，未创建新版本；请先更新版本。";
  }
  if (isHttpClientError(error) && error.code === "CUSTOMIZATION_AI_GENERATION_FAILED") {
    return "AI 生成失败，没有创建定制需求；勾选记录已保留，可重试。";
  }
  if (isHttpClientError(error) && error.code === "CUSTOMIZATION_NO_RECOGNIZED_CHANGES") {
    return "追溯记录未识别到可更新内容，未创建版本；请写清要修改的字段和值。";
  }
  if (isHttpClientError(error) && error.code === "CUSTOMIZATION_TRACE_RECOGNITION_INCOMPLETE") {
    return "有追溯记录未识别到明确修改，本次未更新或创建任何版本；请取消无效追溯，或补充具体字段和值。";
  }
  if (isHttpClientError(error) && error.code === "CUSTOMIZATION_AI_QUOTA_UNAVAILABLE") {
    return "AI 服务额度或计费异常，未创建定制需求；勾选记录已保留，请处理额度后重试。";
  }
  if (isHttpClientError(error) && error.code === "CUSTOMIZATION_UNAVAILABLE") {
    return "后端依赖未就绪，未创建新版本；请检查回写任务表和服务配置后重试。";
  }
  if (isHttpClientError(error) && error.code === "CUSTOMIZATION_PROJECT_IDENTITY_INVALID") {
    return "出发高校/组织、客户、客户联系人不匹配，请重新选择后保存。";
  }
  if (isHttpClientError(error) && error.code === "CUSTOMIZATION_OPPORTUNITY_STALE") {
    return "项目或需求刚被更新，请确认最新内容后重试归属。";
  }
  if (isHttpClientError(error) && error.code === "CUSTOMIZATION_OPPORTUNITY_DISABLED") {
    return "当前商机已停用，不能继续修改定制；请先启用商机后重试。";
  }
  if (isHttpClientError(error) && error.code === "CUSTOMIZATION_OPPORTUNITY_CONFLICT") {
    return "商机归属冲突，请刷新后重新选择已有商机或创建独立商机。";
  }
  if (isHttpClientError(error) && error.code === "CUSTOMIZATION_OPPORTUNITY_FORBIDDEN") {
    return "无权绑定所选商机，请更换候选或联系管理员。";
  }
  if (isHttpClientError(error) && error.code === "CUSTOMIZATION_OPPORTUNITY_INVALID_DECISION") {
    return "归属决定无效，请重新选择商机并填写必要原因后重试。";
  }
  if (isHttpClientError(error) && error.code === "CUSTOMIZATION_OPPORTUNITY_WRITEBACK_FAILED") {
    return "商机归属写回失败，本次未保存；请稍后重试。";
  }
  if (isHttpClientError(error) && error.code === "CUSTOMIZATION_OPPORTUNITY_BINDING_REQUIRED") {
    return "请先完成商机归属后再继续后续业务。";
  }
  if (isHttpClientError(error) && error.code === "CUSTOMIZATION_OPPORTUNITY_REVIEW_REQUIRED") {
    return "学校/组织或联系人已变化，请先复核商机归属。";
  }
  if (isHttpClientError(error)) {
    const message = error.message.trim();
    if (message && !/^customization-opportunity-/i.test(message) && !isGenericHttpFailureMessage(message)) {
      return message;
    }
    if (error.statusCode === 409) {
      return "商机归属冲突或内容已过期，请刷新后重试。";
    }
  }
  return "操作失败，请检查内容后重试。";
}

class ItineraryGenerationBlockedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ItineraryGenerationBlockedError";
  }
}
